import React, { useState } from "react";
import styled from "styled-components";
import Silo, { siloFillColor } from "./Silo";

const SiloRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 4px;
`;

const SiloButton = styled.button`
  position: relative;
  flex: 1 0 84px;
  min-width: 84px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4px 3px 6px;
  border: 1px solid ${({ active }) => (active ? "rgba(255, 255, 255, 0.22)" : "transparent")};
  border-radius: 8px;
  background-color: ${({ active }) => (active ? "rgba(255, 255, 255, 0.1)" : "transparent")};
  color: #fff;
  cursor: pointer;
  text-align: center;

  & + & {
    margin-left: 6px;
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.07);
  }

  &:focus {
    outline: none;
  }
`;

const Art = styled.div`
  position: relative;
  width: 66px;
`;

const Badge = styled.span`
  position: absolute;
  top: 0px;
  right: -3px;
  width: 17px;
  height: 17px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
  background-color: ${({ warn }) => (warn ? "#FFAA00" : "#6D7A88")};
  color: ${({ warn }) => (warn ? "#3D2A00" : "rgba(255, 255, 255, 0.9)")};
  font-family: "Rubik", sans-serif;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
`;

const RefillBadge = styled.span`
  position: absolute;
  top: 0px;
  left: -3px;
  width: 17px;
  height: 17px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
  background-color: #ff9500;
  color: #3d2a00;
  font-family: "Rubik", sans-serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
`;

const Name = styled.span`
  max-width: 100%;
  overflow: hidden;
  color: ${({ dimmed }) => (dimmed ? "rgba(255, 255, 255, 0.46)" : "rgba(255, 255, 255, 0.95)")};
  font-family: "Rubik", sans-serif;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
`;

const Detail = styled.div`
  width: 100%;
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.16);
  display: flex;
  flex-direction: column;
`;

const Percentage = styled.span`
  color: ${({ color }) => color};
  font-family: "Rubik", sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
`;

const Current = styled.span`
  color: rgba(255, 255, 255, 0.9);
  font-family: "Rubik", sans-serif;
  font-size: 11px;
  font-weight: 500;
`;

const Capacity = styled.span`
  color: rgba(255, 255, 255, 0.55);
  font-family: "Rubik", sans-serif;
  font-size: 10px;
`;

const Message = styled.span`
  color: rgba(255, 255, 255, 0.68);
  font-family: "Rubik", sans-serif;
  font-size: 10px;
  line-height: 1.4;
  text-align: left;

  strong {
    display: block;
    color: ${({ warn }) => (warn ? "#FFC75A" : "rgba(255, 255, 255, 0.75)")};
    font-weight: 700;
  }
`;

const Empty = styled.p`
  margin: 12px 0px;
  color: rgba(255, 255, 255, 0.6);
  font-family: "Rubik", sans-serif;
  font-size: 12px;
  text-align: center;
`;

// Seta para cima = nível subindo (silo abastecendo). SVG em vez de glyph (↻ virava
// anzol no Rubik neste tamanho) para ler nítido em ~10px e não depender da fonte.
const RefillIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20V7" />
    <path d="M6 13l6-6 6 6" />
  </svg>
);

const formatWeight = (value) => `${Math.round(value).toLocaleString("pt-BR")} kg`;

// Um silo é monitorado quando o local aponta para um sensor de nível (monitor_sensor),
// papel distinto da balança de dosagem (sensor). Local só com balança conta como não monitorado.
const isMonitored = (location) => Boolean(location.monitorSensor);

export const hasMonitoredSilo = (locations = []) => locations.some(isMonitored);

const SiloLevels = ({ locations = [], statusById = {} }) => {
  const [activeId, setActiveId] = useState(null);

  if (!locations.length) {
    return <Empty>Nenhum silo de cimento cadastrado.</Empty>;
  }

  return (
    <SiloRow>
      {locations.map((location) => {
        const monitored = isMonitored(location);
        // capacity vem do scalar :decimal, ou seja, string.
        const capacity = parseFloat(location.capacity);
        const hasCapacity = Number.isFinite(capacity) && capacity > 0;
        const current = monitored && location.monitorSensor.lastMeasurement != null
          ? location.monitorSensor.lastMeasurement
          : null;
        const missingCapacity = monitored && !hasCapacity;
        const percentage = (monitored && hasCapacity && current !== null)
          ? (current / capacity) * 100
          : null;
        const active = activeId === location.id;
        const refilling = statusById[location.id] === "refilling";

        return (
          <SiloButton
            key={location.id}
            type="button"
            active={active}
            aria-pressed={active}
            onClick={() => setActiveId(active ? null : location.id)}
          >
            <Art>
              <Silo
                id={location.id}
                percentage={percentage}
                missingCapacity={missingCapacity}
                monitored={monitored}
              />
              {!monitored && <Badge>&times;</Badge>}
              {missingCapacity && <Badge warn>!</Badge>}
              {refilling && <RefillBadge title="Abastecendo"><RefillIcon /></RefillBadge>}
            </Art>

            <Name dimmed={!monitored}>{location.name}</Name>

            {active && (
              <Detail>
                {refilling && (
                  <Message warn>
                    <strong>Abastecendo</strong>
                    Entrada de material em andamento — carregamento de cimento aguardando.
                  </Message>
                )}

                {!monitored && (
                  <Message>
                    <strong>Não monitorado</strong>
                    Este local de estoque não tem sensor de silo vinculado.
                  </Message>
                )}

                {missingCapacity && (
                  <React.Fragment>
                    {current !== null && <Current>{formatWeight(current)}</Current>}
                    <Message warn>
                      <strong>Capacidade não cadastrada</strong>
                      Cadastre a capacidade do local de estoque para ver o nível do silo.
                    </Message>
                  </React.Fragment>
                )}

                {monitored && !missingCapacity && percentage === null && (
                  <Message>
                    <strong>Sem leitura</strong>
                    Aguardando a primeira medição do sensor de silo.
                  </Message>
                )}

                {percentage !== null && (
                  <React.Fragment>
                    <Percentage color={siloFillColor(percentage)}>
                      {`${Math.round(percentage)}%`}
                    </Percentage>
                    <Current>{formatWeight(current)}</Current>
                    <Capacity>{`de ${formatWeight(capacity)}`}</Capacity>
                  </React.Fragment>
                )}
              </Detail>
            )}
          </SiloButton>
        );
      })}
    </SiloRow>
  );
};

export default SiloLevels;
